import type { Brand } from '@/brand/types'
import { decodeBrand } from '@/brand/decode'
import {
  decodePage,
  isRecord,
  nullableNumber,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
  withQuery,
} from '@/lib/api'
import type { PagedResponse } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import {
  decodeActivityEntryBase,
  type ActivityEntryBase,
} from '@/modules/audit'

type Schemas = components['schemas']

export type EntityBranding = Omit<Schemas['EntityBrandingResponse'], 'value' | 'isPolicyValue'> & { value: Brand }

function decodeEntityBranding(raw: unknown): EntityBranding {
  if (!isRecord(raw)) throw new Error('Malformed entity branding')
  const refreshStatus = raw.refreshStatus
  if (
    refreshStatus !== 'Pending'
    && refreshStatus !== 'Running'
    && refreshStatus !== 'Current'
    && refreshStatus !== 'Failed'
    && refreshStatus !== 'Unknown'
  ) {
    throw new Error('Unexpected branding refresh status')
  }
  return { value: decodeBrand(raw.value), refreshStatus } satisfies EntityBranding
}

export type Ping = Schemas['PingResponse']


function decodePing(raw: unknown): Ping {
  if (!isRecord(raw)) throw new Error('Malformed ping response')
  return {
    message: requireString(raw.message, 'message'),
    serverTime: requireString(raw.serverTime, 'serverTime'),
    buildVersion: requireString(raw.buildVersion, 'buildVersion'),
    hostingEnvironment: requireString(
      raw.hostingEnvironment,
      'hostingEnvironment',
    ),
    user: requireString(raw.user, 'user'),
  } satisfies Ping
}

export type Entity = Schemas['EntityDto']

export type Ancestor = Schemas['AncestorResponse']

export type AncestorsResponse = Schemas['AncestorsResponse']

export type UserManagementEntity = Schemas['UserManagementEntityResponse']

function decodeUserManagementEntity(raw: unknown): UserManagementEntity {
  if (!isRecord(raw)) throw new Error('Malformed user-management-entity response')
  return {
    userManagementEntityId: nullableString(raw.userManagementEntityId, 'userManagementEntityId'),
    userManagementEntityName: nullableString(raw.userManagementEntityName, 'userManagementEntityName'),
  } satisfies UserManagementEntity
}

export type CreateEntityInput = Omit<
  Schemas['CreateEntityRequest'],
  'parentEntityId' | 'canManageUsers'
> & {
  parentEntityId: string
  canManageUsers?: boolean
}

export type MoveEntityInput = Schemas['MoveEntityRequest'] & {
  newParentEntityId: string
}

export type MoveEntityResult = Schemas['MoveEntityResponse']

export type CancelledInvitation = Schemas['CancelledInvitation']

function decodeCancelledInvitations(raw: unknown): CancelledInvitation[] {
  if (!Array.isArray(raw)) throw new Error('Malformed cancelled invitations')
  return raw.map((value) => {
    if (!isRecord(value)) throw new Error('Malformed cancelled invitation')
    return {
      invitationId: requireString(value.invitationId, 'invitationId'),
      invitedEmail: nullableString(value.invitedEmail, 'invitedEmail'),
    } satisfies CancelledInvitation
  })
}

function decodeMoveImpact(raw: unknown): CancelledInvitation[] {
  if (!isRecord(raw)) throw new Error('Malformed move impact response')
  return decodeCancelledInvitations(raw.cancelledInvitations)
}

export type EntityPathNode = Schemas['EntitySearchPathNode']

export type EntitySearchResult = Schemas['EntitySearchResponse']

function decodeEntity(raw: unknown): Entity {
  if (!isRecord(raw)) throw new Error('Malformed entity response')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    name: requireString(raw.name, 'name'),
    parentEntityId: nullableString(raw.parentEntityId, 'parentEntityId'),
    hasChildren: requireBoolean(raw.hasChildren, 'hasChildren'),
    canManageUsers: raw.canManageUsers === true,
  } satisfies Entity
}

function decodeAncestor(raw: unknown): Ancestor {
  if (!isRecord(raw)) throw new Error('Malformed ancestor')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    name: requireString(raw.name, 'name'),
    depthFromSelected: requireNumber(
      raw.depthFromSelected,
      'depthFromSelected',
    ),
    hasChildren: requireBoolean(raw.hasChildren, 'hasChildren'),
  } satisfies Ancestor
}

function decodeAncestors(raw: unknown): AncestorsResponse {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed ancestors response')
  return { items: raw.items.map(decodeAncestor) } satisfies AncestorsResponse
}

function decodeEntityList(raw: unknown): Entity[] {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed roots response')
  return raw.items.map(decodeEntity)
}

const decodeEntityPage = (raw: unknown): PagedResponse<Entity> =>
  decodePage(raw, decodeEntity, 'paged entity response')

function decodeMoveResult(raw: unknown): MoveEntityResult {
  if (!isRecord(raw)) throw new Error('Malformed move response')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    previousParentEntityId: nullableString(
      raw.previousParentEntityId,
      'previousParentEntityId',
    ),
    newParentEntityId: nullableString(
      raw.newParentEntityId,
      'newParentEntityId',
    ),
    moved: requireBoolean(raw.moved, 'moved'),
    cancelledInvitations: decodeCancelledInvitations(raw.cancelledInvitations),
  } satisfies MoveEntityResult
}

function decodePathNode(raw: unknown): EntityPathNode {
  if (!isRecord(raw)) throw new Error('Malformed path node')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    name: requireString(raw.name, 'name'),
  } satisfies EntityPathNode
}

function decodeSearchResult(raw: unknown): EntitySearchResult {
  if (!isRecord(raw) || !Array.isArray(raw.pathFromRoot))
    throw new Error('Malformed search result')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    name: requireString(raw.name, 'name'),
    parentEntityId: nullableString(raw.parentEntityId, 'parentEntityId'),
    pathFromRoot: raw.pathFromRoot.map(decodePathNode),
  } satisfies EntitySearchResult
}

const decodeSearchPage = (
  raw: unknown,
): PagedResponse<EntitySearchResult> =>
  decodePage(raw, decodeSearchResult, 'search response')

export type EntitySetting = Omit<Schemas['EntitySettingResponse'], 'isPolicyValue'> & { isPolicyValue?: boolean }

export type CapabilityKey = Schemas['PutCapabilityChange']['key']

export type EntityCapability = Schemas['CapabilityState']

export type CapabilityChange = Schemas['PutCapabilityChange']

function decodeEntityCapability(raw: unknown): EntityCapability {
  if (!isRecord(raw) || !Array.isArray(raw.violations))
    throw new Error('Malformed capability item')
  const key = requireString(raw.key, 'key')
  return {
    key,
    name: requireString(raw.name, 'name'),
    description: requireString(raw.description, 'description'),
    canDisable: requireBoolean(raw.canDisable, 'canDisable'),
    enableConfirmation: nullableString(raw.enableConfirmation, 'enableConfirmation'),
    enabled: requireBoolean(raw.enabled, 'enabled'),
    canToggle: requireBoolean(raw.canToggle, 'canToggle'),
    violations: raw.violations.map((value) => {
      if (!isRecord(value) || !Array.isArray(value.details))
        throw new Error('Malformed capability violation')
      return {
        type: requireString(value.type, 'violation type'),
        rule: requireString(value.rule, 'violation rule'),
        details: value.details.map((detail) =>
          requireString(detail, 'violation detail'),
        ),
      }
    }),
    cancelledInvitations: decodeCancelledInvitations(raw.cancelledInvitations),
  } satisfies EntityCapability
}

function decodeEntityCapabilities(raw: unknown): EntityCapability[] {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed capabilities response')
  return raw.items.map(decodeEntityCapability)
}

export type SettingWriteItem = Schemas['PutEntitySettingItem'] & {
  key: string
  value: unknown
}

export type BrandLogoUpload = Schemas['BrandLogoUploadResponse']

function decodeBrandLogoUpload(raw: unknown): BrandLogoUpload {
  if (!isRecord(raw)) throw new Error('Malformed brand-logo upload response')
  return {
    url: requireString(raw.url, 'url'),
    contentType: requireString(raw.contentType, 'contentType'),
    sizeBytes: requireNumber(raw.sizeBytes, 'sizeBytes'),
  } satisfies BrandLogoUpload
}

function decodeEntitySetting(raw: unknown): EntitySetting {
  if (!isRecord(raw)) throw new Error('Malformed setting item')
  return {
    key: requireString(raw.key, 'key'),
    effectiveValue: raw.effectiveValue,
    sourceEntityId: nullableString(raw.sourceEntityId, 'sourceEntityId'),
    inheritedDistance: requireNumber(raw.inheritedDistance, 'inheritedDistance'),
    isPolicyValue: raw.isPolicyValue === undefined ? false : requireBoolean(raw.isPolicyValue, 'isPolicyValue'),
  } satisfies EntitySetting
}

function decodeEntitySettings(raw: unknown): EntitySetting[] {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed settings response')
  return raw.items.map(decodeEntitySetting)
}

export type ResourceSettingDefault = Schemas['ResourceSettingDefaultResponse']

const RESOURCE_SETTING_SOURCES = ['resource', 'entity', 'default', 'unresolved'] as const

function decodeEnum<T extends string>(raw: unknown, field: string, allowed: readonly T[]): T {
  const value = requireString(raw, field)
  if (allowed.includes(value as T)) return value as T
  throw new Error(`Unexpected ${field} "${value}"`)
}

function decodeResourceSettingDefault(raw: unknown): ResourceSettingDefault {
  if (!isRecord(raw)) throw new Error('Malformed resource setting default')
  return {
    key: requireString(raw.key, 'key'),
    value: raw.value ?? null,
    sourceEntityId: nullableString(raw.sourceEntityId, 'sourceEntityId'),
    inheritedDistance: nullableNumber(raw.inheritedDistance, 'inheritedDistance'),
    source: decodeEnum(raw.source, 'source', RESOURCE_SETTING_SOURCES),
  } satisfies ResourceSettingDefault
}

function decodeResourceSettingDefaults(raw: unknown): ResourceSettingDefault[] {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed resource setting defaults response')
  return raw.items.map(decodeResourceSettingDefault)
}

const resourceSettingDefaultPath = (
  entityId: string,
  resourceType: string,
  settingKey: string,
) =>
  `/api/v1/entities/${entityId}/resource-settings/${encodeURIComponent(resourceType)}/${encodeURIComponent(settingKey)}`

const resourceSettingPath = (
  entityId: string,
  resourceType: string,
  resourceId: string,
  settingKey: string,
) =>
  `/api/v1/entities/${entityId}/resources/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}/settings/${encodeURIComponent(settingKey)}`

export type SettingHistoryEntry = ActivityEntryBase

export type SettingHistoryQuery = {
  search?: string
  actorId?: string
  actorType?: string
  from?: string
  to?: string
  source?: 'all' | 'local' | 'inherited'
  after?: string
}

function decodeSettingHistoryEntry(raw: unknown): SettingHistoryEntry {
  if (!isRecord(raw)) throw new Error('Malformed setting history entry')
  return decodeActivityEntryBase(raw)
}

const decodeSettingHistoryPage = (
  raw: unknown,
): PagedResponse<SettingHistoryEntry> =>
  decodePage(raw, decodeSettingHistoryEntry, 'setting history page')

export const hierarchyApi = {
  getEntityBranding: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/branding`, decodeEntityBranding),
  ping: () => request('/api/v1/ping', decodePing),
  getRoots: (signal?: AbortSignal) => request('/api/v1/entities/roots', decodeEntityList, { signal }),
  getEntity: (entityId: string) => request(`/api/v1/entities/${entityId}`, decodeEntity),
  getAncestors: (entityId: string) => request(`/api/v1/entities/${entityId}/ancestors`, decodeAncestors),
  getUserManagementEntity: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/user-management-entity`, decodeUserManagementEntity),
  getEntityChildren: (entityId: string, after?: string) => {
    const query = new URLSearchParams()
    if (after) query.set('after', after)
    const qs = query.toString()
    return request(`/api/v1/entities/${entityId}/children${qs ? `?${qs}` : ''}`, decodeEntityPage)
  },
  searchEntities: (entityId: string, query: string) =>
    request(`/api/v1/entities/${entityId}/search?${new URLSearchParams({ q: query })}`, decodeSearchPage),
  getSettingHistory: (
    entityId: string,
    settingKey: string,
    query: SettingHistoryQuery,
  ) =>
    request(
      `/api/v1/entities/${entityId}/settings/${encodeURIComponent(settingKey)}/history:query`,
      decodeSettingHistoryPage,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(query),
      },
    ),
  createEntity: (input: CreateEntityInput) =>
    request('/api/v1/entities', decodeEntity, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  renameEntity: (entityId: string, name: string) =>
    request(`/api/v1/entities/${entityId}`, decodeEntity, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json-patch+json' },
      body: JSON.stringify([{ op: 'replace', path: '/name', value: name }]),
    }),
  moveEntity: (entityId: string, input: MoveEntityInput) =>
    request(`/api/v1/entities/${entityId}/move`, decodeMoveResult, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  previewMove: (entityId: string, newParentEntityId: string) =>
    request(
      `/api/v1/entities/${entityId}/move-impact?${new URLSearchParams({ newParentEntityId })}`,
      decodeMoveImpact,
    ),
  getEntitySettings: (entityId: string) => request(`/api/v1/entities/${entityId}/settings`, decodeEntitySettings),
  getEntityCapabilities: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/capabilities`, decodeEntityCapabilities),
  putEntityCapabilities: (
    entityId: string,
    items: CapabilityChange[],
    expectedCancelledInvitationIds?: string[],
  ) =>
    request(`/api/v1/entities/${entityId}/capabilities`, decodeEntityCapabilities, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, expectedCancelledInvitationIds } satisfies Schemas['PutCapabilitiesRequest']),
    }),
  uploadBrandLogo: (entityId: string, file: File) =>
    request(`/api/v1/entities/${entityId}/branding/logo`, decodeBrandLogoUpload, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    }),
  putEntitySettings: (entityId: string, items: SettingWriteItem[]) =>
    requestNoContent(`/api/v1/entities/${entityId}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items } satisfies Schemas['PutEntitySettingsRequest']),
    }),
  deleteEntitySetting: (entityId: string, settingKey: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/settings/${encodeURIComponent(settingKey)}`, { method: 'DELETE' }),
  getResourceSettingDefaults: (entityId: string, resourceType: string) =>
    request(
      `/api/v1/entities/${entityId}/resource-settings/${encodeURIComponent(resourceType)}`,
      decodeResourceSettingDefaults,
    ),
  putResourceSettingDefault: (
    entityId: string,
    resourceType: string,
    settingKey: string,
    value: unknown,
  ) =>
    requestNoContent(resourceSettingDefaultPath(entityId, resourceType, settingKey), {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value } satisfies Schemas['PutResourceSettingDefaultRequest']),
    }),
  deleteResourceSettingDefault: (entityId: string, resourceType: string, settingKey: string) =>
    requestNoContent(resourceSettingDefaultPath(entityId, resourceType, settingKey), { method: 'DELETE' }),
  getResourceSettingDefaultHistory: (
    entityId: string,
    resourceType: string,
    settingKey: string,
    after?: string,
  ) =>
    request(
      withQuery(`${resourceSettingDefaultPath(entityId, resourceType, settingKey)}/history`, { after }),
      decodeSettingHistoryPage,
    ),
  putResourceSetting: (
    entityId: string,
    resourceType: string,
    resourceId: string,
    settingKey: string,
    value: unknown,
  ) =>
    requestNoContent(resourceSettingPath(entityId, resourceType, resourceId, settingKey), {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value } satisfies Schemas['PutResourceSettingRequest']),
    }),
  deleteResourceSetting: (
    entityId: string,
    resourceType: string,
    resourceId: string,
    settingKey: string,
  ) =>
    requestNoContent(resourceSettingPath(entityId, resourceType, resourceId, settingKey), {
      method: 'DELETE',
    }),
  getResourceSettingHistory: (
    entityId: string,
    resourceType: string,
    resourceId: string,
    settingKey: string,
    after?: string,
  ) =>
    request(
      withQuery(`${resourceSettingPath(entityId, resourceType, resourceId, settingKey)}/history`, {
        after,
      }),
      decodeSettingHistoryPage,
    ),
}
